import React, { useState } from 'react';

const ModuleEnhancementWizard = ({ module, onOpenChat, onBack }) => {
  const [activeStep, setActiveStep] = useState(null);
  
  const completionStatus = module.completionStatus || {
    routes: false,
    database: false,
    ui: false,
    realtime: false,
    permissions: false,
    widget: false
  };

  const coreComplete = completionStatus.routes && completionStatus.database;

  const steps = [
    {
      id: 'routes',
      icon: '🔌',
      label: 'Add API Routes',
      description: 'Create backend endpoints for CRUD operations',
      required: true,
      complete: completionStatus.routes,
      order: 1
    },
    {
      id: 'database',
      icon: '🗄️',
      label: 'Connect Database',
      description: 'Set up MongoDB collections and schemas',
      required: true,
      complete: completionStatus.database,
      order: 2,
      locked: !completionStatus.routes
    },
    {
      id: 'ui',
      icon: '🎨',
      label: 'Enhance UI',
      description: 'Improve styling and user experience',
      required: false,
      complete: completionStatus.ui,
      order: 3,
      locked: !coreComplete
    },
    {
      id: 'realtime',
      icon: '⚡',
      label: 'Real-time Updates',
      description: 'Add WebSocket support for live data',
      required: false,
      complete: completionStatus.realtime,
      order: 4,
      locked: !coreComplete
    },
    {
      id: 'permissions',
      icon: '🔒',
      label: 'Permissions',
      description: 'Role-based access control',
      required: false,
      complete: completionStatus.permissions,
      order: 5,
      locked: !coreComplete
    },
    {
      id: 'widget',
      icon: '📊',
      label: 'Dashboard Widget',
      description: 'Add module summary to main dashboard',
      required: false,
      complete: completionStatus.widget,
      order: 6,
      locked: !coreComplete
    }
  ];

  const requiredSteps = steps.filter(s => s.required);
  const optionalSteps = steps.filter(s => !s.required);
  const nextRequiredStep = requiredSteps.find(s => !s.complete);
  const completedCount = steps.filter(s => s.complete).length;
  const progressPercent = Math.round((completedCount / steps.length) * 100);

  const handleStepClick = (step) => {
    if (step.locked) return;
    
    const prompt = buildPromptForStep(step);
    setActiveStep(step.id);
    
    // Open chat with pre-filled context
    if (onOpenChat) {
      onOpenChat(prompt);
    }
  };

  const buildPromptForStep = (step) => {
    const baseContext = `I'm working on the "${module.name}" module.`;
    
    switch (step.id) {
      case 'routes':
        return `${baseContext} Please add API routes for this module. The module should have full CRUD operations (Create, Read, Update, Delete) with proper error handling and validation.`;
      case 'database':
        return `${baseContext} Please connect this module to MongoDB. Create the necessary schema/model and update the routes to use the database instead of mock data.`;
      case 'ui':
        return `${baseContext} Please enhance the UI for this module. Improve the styling, add better loading states, error handling, and make it more user-friendly.`;
      case 'realtime':
        return `${baseContext} Please add real-time updates to this module using WebSockets so changes appear instantly without page refresh.`;
      case 'permissions':
        return `${baseContext} Please add role-based permissions to this module. Only managers should be able to create/edit/delete, while regular users can only view.`;
      case 'widget':
        return `${baseContext} Please create a dashboard widget for this module that shows key metrics and recent activity on the main Executive Dashboard.`;
      default:
        return baseContext;
    }
  };

  const StepCard = ({ step, isNext }) => {
    const isLocked = step.locked;
    const isComplete = step.complete;
    
    return (
      <div
        onClick={() => !isLocked && handleStepClick(step)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
          padding: '16px 20px',
          background: isComplete 
            ? 'rgba(34, 197, 94, 0.1)' 
            : isNext 
              ? 'rgba(59, 130, 246, 0.2)' 
              : isLocked 
                ? 'rgba(100, 116, 139, 0.1)' 
                : 'rgba(59, 130, 246, 0.1)',
          border: isNext 
            ? '2px solid #3b82f6' 
            : isComplete 
              ? '1px solid rgba(34, 197, 94, 0.3)' 
              : '1px solid rgba(59, 130, 246, 0.2)',
          borderRadius: '12px',
          cursor: isLocked ? 'not-allowed' : 'pointer',
          opacity: isLocked ? 0.5 : 1,
          transition: 'all 0.2s ease',
          position: 'relative'
        }}
      >
        <div style={{
          fontSize: '28px',
          filter: isLocked ? 'grayscale(1)' : 'none'
        }}>
          {isComplete ? '✅' : step.icon}
        </div>
        
        <div style={{ flex: 1 }}>
          <div style={{
            color: isComplete ? '#22c55e' : '#e2e8f0',
            fontWeight: '600',
            fontSize: '16px',
            marginBottom: '4px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            {step.label}
            {step.required && !isComplete && (
              <span style={{
                background: 'rgba(239, 68, 68, 0.2)',
                color: '#ef4444',
                padding: '2px 8px',
                borderRadius: '4px',
                fontSize: '11px',
                fontWeight: '500'
              }}>
                Required
              </span>
            )}
            {isNext && (
              <span style={{
                background: 'rgba(59, 130, 246, 0.3)',
                color: '#60a5fa',
                padding: '2px 8px',
                borderRadius: '4px',
                fontSize: '11px',
                fontWeight: '500'
              }}>
                Start Here
              </span>
            )}
          </div>
          <div style={{
            color: '#94a3b8',
            fontSize: '14px'
          }}>
            {step.description}
          </div>
        </div>

        {isLocked && (
          <div style={{ color: '#64748b', fontSize: '20px' }}>🔒</div>
        )}
        
        {!isLocked && !isComplete && (
          <div style={{
            color: '#3b82f6',
            fontSize: '14px',
            fontWeight: '500'
          }}>
            Build →
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={{
      padding: '32px 48px',
      background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.05), rgba(147, 51, 234, 0.05))',
      minHeight: '100vh'
    }}>
      {/* Header */}
      <div style={{ marginBottom: '32px' }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          marginBottom: '8px'
        }}>
          <span style={{ fontSize: '32px' }}>⚠️</span>
          <h1 style={{
            color: '#e2e8f0',
            fontSize: '28px',
            margin: 0
          }}>
            {module.name}
          </h1>
          <span style={{
            background: 'rgba(245, 158, 11, 0.2)',
            color: '#f59e0b',
            padding: '4px 12px',
            borderRadius: '6px',
            fontSize: '14px',
            fontWeight: '500'
          }}>
            Incomplete
          </span>
        </div>
        <p style={{
          color: '#94a3b8',
          fontSize: '16px',
          margin: '8px 0 0 44px'
        }}>
          {module.description}
        </p>
      </div>

      {/* Progress Bar */}
      <div style={{
        background: 'rgba(30, 41, 59, 0.5)',
        borderRadius: '12px',
        padding: '20px 24px',
        marginBottom: '32px',
        border: '1px solid rgba(59, 130, 246, 0.2)'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '12px'
        }}>
          <span style={{ color: '#e2e8f0', fontWeight: '600' }}>
            Module Completion
          </span>
          <span style={{ color: '#94a3b8' }}>
            {completedCount} of {steps.length} steps
          </span>
        </div>
        <div style={{
          background: 'rgba(100, 116, 139, 0.3)',
          borderRadius: '8px',
          height: '12px',
          overflow: 'hidden'
        }}>
          <div style={{
            background: coreComplete 
              ? 'linear-gradient(90deg, #22c55e, #10b981)' 
              : 'linear-gradient(90deg, #f59e0b, #eab308)',
            height: '100%',
            width: `${progressPercent}%`,
            borderRadius: '8px',
            transition: 'width 0.5s ease'
          }} />
        </div>
        {!coreComplete && (
          <p style={{
            color: '#f59e0b',
            fontSize: '14px',
            marginTop: '12px',
            marginBottom: 0
          }}>
            ⚠️ Complete the required steps (API Routes + Database) to unlock optional enhancements
          </p>
        )}
      </div>

      {/* Required Steps */}
      <div style={{ marginBottom: '32px' }}>
        <h3 style={{
          color: '#e2e8f0',
          fontSize: '18px',
          marginBottom: '16px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          🎯 Required Steps
        </h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {requiredSteps.map((step) => (
            <StepCard 
              key={step.id} 
              step={step} 
              isNext={nextRequiredStep?.id === step.id}
            />
          ))}
        </div>
      </div>

      {/* Optional Steps */}
      <div>
        <h3 style={{
          color: '#e2e8f0',
          fontSize: '18px',
          marginBottom: '16px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          opacity: coreComplete ? 1 : 0.5
        }}>
          ✨ Optional Enhancements
          {!coreComplete && (
            <span style={{
              color: '#64748b',
              fontSize: '14px',
              fontWeight: '400'
            }}>
              (complete required steps first)
            </span>
          )}
        </h3>
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
          gap: '12px' 
        }}>
          {optionalSteps.map((step) => (
            <StepCard key={step.id} step={step} isNext={false} />
          ))}
        </div>
      </div>
    </div>
  );
};

export default ModuleEnhancementWizard;
